import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Space, Breadcrumb, Button, Input, Switch, Tag } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import type { ColumnsType } from "antd/es/table";

import type {
  ListDeviceProfilesResponse,
  DeviceProfileVendor,
  DeviceProfileDevice,
} from "@chirpstack/chirpstack-api-grpc-web/api/device_profile_pb";
import {
  DeleteDeviceProfileDeviceRequest,
  GetDeviceProfileRequest,
  ListDeviceProfilesRequest,
  UpdateDeviceProfileRequest,
  type DeviceProfileListItem,
} from "@chirpstack/chirpstack-api-grpc-web/api/device_profile_pb";
import { Region } from "@chirpstack/chirpstack-api-grpc-web/common/common_pb";

import type { GetPageCallbackFunc } from "../../components/DataTable";
import DataTable from "../../components/DataTable";
import deviceProfileStore from "../../stores/DeviceProfileStore";
import { getEnumName, useTitle, formatMacVersion, formatRegParamsRevision } from "../helpers";
import DeleteConfirm from "../../components/DeleteConfirm";

interface IProps {
  vendor: DeviceProfileVendor;
  device: DeviceProfileDevice;
}

function ListVendorDeviceProfiles(props: IProps) {
  const navigate = useNavigate();
  useTitle("Network Server", "Device Profiles");
  const [refreshKey, setRefreshKey] = useState<number>(1);
  const [search, setSearch] = useState<string>("");
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const isBridge = props.device.getSource() === "bridge";

  // See ListVendors.togglePublished — same get-then-update pattern.
  const togglePublished = (id: string, next: boolean) => {
    setTogglingId(id);
    const getReq = new GetDeviceProfileRequest();
    getReq.setId(id);
    deviceProfileStore.get(getReq, resp => {
      const dp = resp.getDeviceProfile();
      if (!dp) {
        setTogglingId(null);
        return;
      }
      dp.setPublished(next);
      const updReq = new UpdateDeviceProfileRequest();
      updReq.setDeviceProfile(dp);
      deviceProfileStore.update(updReq, () => {
        setTogglingId(null);
        setRefreshKey(k => k + 1);
      });
    });
  };

  const columns: ColumnsType<DeviceProfileListItem.AsObject> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <Link
          to={`/device-profiles/vendors/${props.vendor.getId()}/devices/${props.device.getId()}/profiles/${record.id}`}
        >
          {text}
        </Link>
      ),
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      width: 110,
      render: source =>
        source === "repo" ? <Tag color="blue">Repo</Tag> : <Tag color="green">Bridge</Tag>,
    },
    {
      title: "Published",
      dataIndex: "published",
      key: "published",
      width: 110,
      render: (_published, record) => (
        <Switch
          checked={record.published}
          loading={togglingId === record.id}
          onChange={next => togglePublished(record.id, next)}
        />
      ),
    },
    {
      title: "Firmware",
      dataIndex: "firmwareVersion",
      key: "firmwareVersion",
      width: 130,
    },
    {
      title: "Region",
      dataIndex: "region",
      key: "region",
      width: 130,
      render: (text, record) => {
        return getEnumName(Region, record.region);
      },
    },
    {
      title: "MAC version",
      dataIndex: "macVersion",
      key: "macVersion",
      width: 130,
      render: (text, record) => {
        return formatMacVersion(record.macVersion);
      },
    },
    {
      title: "Revision",
      dataIndex: "regParamsRevision",
      key: "regParamsRevision",
      width: 130,
      render: (text, record) => {
        return formatRegParamsRevision(record.regParamsRevision);
      },
    },
    {
      title: "OTAA",
      dataIndex: "supportsOtaa",
      key: "supportsOtaa",
      width: 80,
      render: (_text, record) => (record.supportsOtaa ? "yes" : "no"),
    },
    {
      title: "Class-B",
      dataIndex: "supportsClassB",
      key: "supportsClassB",
      width: 90,
      render: (_text, record) => (record.supportsClassB ? "yes" : "no"),
    },
    {
      title: "Class-C",
      dataIndex: "supportsClassC",
      key: "supportsClassC",
      width: 90,
      render: (_text, record) => (record.supportsClassC ? "yes" : "no"),
    },
  ];

  const getPage = (
    limit: number,
    offset: number,
    _filters: object,
    _orderBy: string | void,
    _orderByDesc: boolean | void,
    callbackFunc: GetPageCallbackFunc,
  ) => {
    const req = new ListDeviceProfilesRequest();
    req.setLimit(limit);
    req.setOffset(offset);
    req.setDeviceId(props.device.getId());
    req.setGlobalOnly(true);
    req.setSearch(search);

    deviceProfileStore.list(req, (resp: ListDeviceProfilesResponse) => {
      const obj = resp.toObject();
      callbackFunc(obj.totalCount, obj.resultList);
    });
  };

  const deleteDevice = () => {
    const req = new DeleteDeviceProfileDeviceRequest();
    req.setId(props.device.getId());

    deviceProfileStore.deleteDevice(req, () => {
      navigate(`/device-profiles/vendors/${props.vendor.getId()}/devices`);
    });
  };

  return (
    <Space orientation="vertical" style={{ width: "100%" }} size="large">
      <PageHeader
        breadcrumbRender={() => (
          <Breadcrumb
            items={[
              { title: "Network Server" },
              { title: "Device Profiles" },
              { title: <Link to={`/device-profiles/vendors`}>Vendors</Link> },
              {
                title: (
                  <Link to={`/device-profiles/vendors/${props.vendor.getId()}/devices`}>{props.vendor.getName()}</Link>
                ),
              },
              { title: props.device.getName() },
            ]}
          />
        )}
        title={props.device.getName()}
        tags={
          props.device.getSource() === "repo" ? (
            <Tag color="blue">Repo</Tag>
          ) : (
            <Tag color="green">Bridge</Tag>
          )
        }
        extra={[
          <Input.Search
            key="search-profiles"
            placeholder="Search by name"
            allowClear
            onSearch={value => {
              setSearch(value);
              setRefreshKey(k => k + 1);
            }}
            style={{ width: 240 }}
          />,
          <Space key="add-device-profile-edit-delete-device">
            <Button
              type="primary"
              onClick={() =>
                navigate(
                  `/device-profiles/vendors/${props.vendor.getId()}/devices/${props.device.getId()}/profiles/create`,
                )
              }
            >
              Add device profile
            </Button>
            {isBridge && (
              <Button
                onClick={() =>
                  navigate(`/device-profiles/vendors/${props.vendor.getId()}/devices/${props.device.getId()}/edit`)
                }
              >
                Edit device
              </Button>
            )}
            <DeleteConfirm confirm={props.device.getName()} typ="device" onConfirm={deleteDevice}>
              <Button danger type="primary">
                Delete device
              </Button>
            </DeleteConfirm>
          </Space>,
        ]}
      />
      <DataTable columns={columns} getPage={getPage} rowKey="id" refreshKey={refreshKey} />
    </Space>
  );
}

export default ListVendorDeviceProfiles;
